/**
 * ScannersPage — market-wide technical-analysis scanner directory.
 *
 * Grid of scanner cards (slug, name, live count). Click a card to see
 * the matching tickers. Each row deep-links into the company INTEL tab
 * with the triggering signal as context.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, Loader2, ArrowUpRight, ArrowDownRight,
  Activity, Zap, Info, ChevronRight,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import PageHeader from '../../../components/layout/PageHeader';
import SectionNav from '../../../components/layout/SectionNav';
import { researchTabs } from '../../../nav/publicNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/india`;

const fmtCr = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(0)} Cr`;
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${n.toFixed(2)}`;
};

const fmtPrice = (v) => (v == null ? '—' : `₹${Number(v).toFixed(2)}`);

// Emoji/icon badge per direction.
const DirBadge = ({ dir }) => {
  if (dir === 'bull') return <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px]">Bullish</Badge>;
  if (dir === 'bear') return <Badge className="bg-red-500/10 text-red-600 dark:text-red-400 text-[10px]">Bearish</Badge>;
  return <Badge className="bg-slate-500/10 text-slate-500 text-[10px]">Neutral</Badge>;
};

export default function ScannersPage() {
  const navigate = useNavigate();
  const [scanners, setScanners] = useState([]);
  const [active, setActive] = useState(null);          // slug
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRes, setLoadingRes] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/scanners`);
        if (!cancelled) setScanners(data.scanners || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    (async () => {
      setLoadingRes(true);
      try {
        const { data } = await axios.get(`${API}/scanners/${active}`, { params: { limit: 100 } });
        if (!cancelled) setResults(data.results || []);
      } catch (e) {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoadingRes(false);
      }
    })();
    return () => { cancelled = true; };
  }, [active]);

  const group = (dir) => scanners.filter((s) => s.direction === dir);
  const bulls = group('bull');
  const bears = group('bear');
  const neutrals = group('neutral');

  const goToStock = (symbol, signalType) => {
    if (!symbol) return;
    navigate(`/public/india-company/${symbol}?tab=intel&sig=${signalType || ''}`);
  };

  const activeMeta = scanners.find((s) => s.slug === active);

  return (
    <div className="space-y-4">
      <SectionNav tabs={researchTabs} />
      <PageHeader
        title="Technical Scanners"
        description="Market-wide screens on yesterday's and today's price action. Updated daily after market close."
      />

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="rounded-md bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-500">
          {error}
        </div>
      ) : (
        <>
          {/* Scanner grid */}
          <section className="space-y-5">
            <ScannerGroup title="Bullish" icon={TrendingUp} accent="emerald" items={bulls}
              activeSlug={active} onPick={setActive} />
            <ScannerGroup title="Bearish" icon={TrendingDown} accent="red" items={bears}
              activeSlug={active} onPick={setActive} />
            {neutrals.length > 0 && (
              <ScannerGroup title="Context" icon={Activity} accent="slate" items={neutrals}
                activeSlug={active} onPick={setActive} />
            )}
          </section>

          {/* Active scan results */}
          {active && (
            <Card className="border-emerald-500/20">
              <CardContent className="p-0">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
                  <div>
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-emerald-500" />
                      <span className="font-semibold">{activeMeta?.name || active}</span>
                      {activeMeta && <DirBadge dir={activeMeta.direction} />}
                    </div>
                    {activeMeta?.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                        <Info className="w-3 h-3" /> {activeMeta.description}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setActive(null)}
                    className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 border border-border rounded-md"
                  >
                    Close
                  </button>
                </div>

                {loadingRes ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : results.length === 0 ? (
                  <div className="py-16 text-center text-muted-foreground text-sm">
                    No matches. The scanner will refresh after today's market close.
                  </div>
                ) : (
                  <div className="divide-y divide-border/40 max-h-[520px] overflow-y-auto">
                    {results.map((r, idx) => (
                      <button
                        key={`${r.isin}_${idx}`}
                        onClick={() => goToStock(r.symbol, r.metadata?.signal_type || r.signal_type)}
                        className="w-full text-left px-4 py-2 hover:bg-muted/30 transition-colors flex items-center gap-3"
                      >
                        <span className="font-mono text-[11px] text-muted-foreground w-8">
                          {String(r.rank != null ? r.rank + 1 : idx + 1).padStart(2, '0')}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold">{r.symbol || '—'}</span>
                            <span className="text-xs text-muted-foreground truncate">{r.company_name}</span>
                          </div>
                          <div className="text-[10px] text-muted-foreground">
                            {r.metadata?.detail && typeof r.metadata.detail === 'object'
                              ? Object.entries(r.metadata.detail)
                                  .slice(0, 3)
                                  .map(([k, v]) => `${k}: ${typeof v === 'number' ? v.toFixed ? v.toFixed(2) : v : v}`)
                                  .join(' · ')
                              : Object.entries(r.metadata || {})
                                  .filter(([k]) => !['signal_type', 'direction', 'strength', 'date'].includes(k))
                                  .slice(0, 3)
                                  .map(([k, v]) => `${k}: ${typeof v === 'number' ? v.toFixed ? v.toFixed(2) : v : v}`)
                                  .join(' · ')}
                          </div>
                        </div>
                        <span className="text-xs font-semibold">{fmtPrice(r.price)}</span>
                        <span className="text-[10px] text-muted-foreground w-20 text-right">{fmtCr(r.market_cap)}</span>
                        <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}


function ScannerGroup({ title, icon: Icon, accent, items, activeSlug, onPick }) {
  if (!items || items.length === 0) return null;
  const accentClass = accent === 'emerald'
    ? 'text-emerald-500'
    : accent === 'red'
      ? 'text-red-500'
      : 'text-slate-400';
  const ArrowIcon = accent === 'emerald' ? ArrowUpRight : accent === 'red' ? ArrowDownRight : Activity;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${accentClass}`} />
        <span className="text-sm font-semibold">{title}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
        {items.map((s) => (
          <button
            key={s.slug}
            onClick={() => onPick(s.slug)}
            className={`text-left rounded-lg border p-3 transition-all ${
              activeSlug === s.slug
                ? 'border-emerald-500/60 bg-emerald-500/5'
                : 'border-border hover:border-emerald-500/30 hover:bg-muted/20'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <ArrowIcon className={`w-3.5 h-3.5 ${accentClass}`} />
                  <span className="text-sm font-medium truncate">{s.name}</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{s.description}</p>
              </div>
              <span className={`text-lg font-bold ${accentClass}`}>{s.count || 0}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
